import { useState, type ReactNode } from 'react';
import { ArrowLeftRight, Scale, CalendarClock, PackageCheck, MessageSquareText, Info, X, Plus } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import { Switch } from '@/components/ui/Switch';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { LoadingState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/cn';
import { useBorrowSettingsAdmin, useUpdateBorrowSettings } from '@/features/pharmacy/borrow/useBorrow';
import { SETTLEMENT_LABEL } from '@/features/pharmacy/borrow/ui';
import type { BorrowSettings, BorrowSettingsInput, SettlementType } from '@/features/pharmacy/borrow/types';
import type { PaymentMode } from '@/features/pharmacy/types';

const ALL_MODES: PaymentMode[] = ['Cash', 'UPI', 'Card'];

/** Blank means "no limit" for the optional numeric caps. */
const optionalInt = (v: string): number | null => (v.trim() === '' ? null : Math.max(0, parseInt(v, 10) || 0));

/**
 * Admin Panel → 💊 Pharmacy → 🔁 Medicine Borrow & Return — the hospital's policy for
 * lending medicine to other hospitals in an emergency.
 *
 * Every rule here is enforced by the server (utils/pharmacyBorrow.js), not just
 * reflected in the Borrow screen, and applies from the next action onward —
 * records already issued keep the value, dates and settlement type they were
 * issued with.
 *
 * The master switch saves immediately, like Loyalty Points: it is the one
 * control whose effect staff notice at once. Switching it off stops NEW issues
 * only — medicine already lent can still be returned or paid for, because
 * stranding stock another hospital is holding is never the intent.
 */
export default function BorrowSettingsPanel() {
  const query = useBorrowSettingsAdmin();
  const update = useUpdateBorrowSettings();
  const [draft, setDraft] = useState<BorrowSettingsInput>({});
  const [saved, setSaved] = useState(false);
  const [newReason, setNewReason] = useState('');

  const settings = query.data;
  const value = <K extends keyof BorrowSettings>(key: K): BorrowSettings[K] | undefined =>
    (key in draft ? (draft as BorrowSettings)[key] : settings?.[key]);
  const set = (patch: BorrowSettingsInput) => { setDraft((d) => ({ ...d, ...patch })); setSaved(false); };
  const dirty = Object.keys(draft).length > 0;

  const save = (patch: BorrowSettingsInput, clearDraft: boolean) =>
    update.mutate(patch, {
      onSuccess: () => { if (clearDraft) setDraft({}); setSaved(true); },
    });

  if (!settings) {
    return query.error
      ? <div className="rounded-lg bg-danger-soft px-3 py-2 text-xs text-danger-ink">{query.error instanceof ApiError ? query.error.message : 'Could not load the settings.'}</div>
      : <LoadingState />;
  }

  const allowReturn = value('allow_item_return') ?? true;
  const allowPayment = value('allow_payment') ?? true;
  const defaultType = value('default_settlement_type') ?? 'item_return';
  const modes = value('allowed_payment_modes') ?? ALL_MODES;
  const reasons = value('reason_presets') ?? [];
  const enabled = settings.enabled;

  // Mirrors the server's own cross-field rules, so the admin sees the problem
  // before pressing Save rather than as a 400 afterwards.
  const localError =
    !allowReturn && !allowPayment ? 'At least one settlement type must stay allowed.'
    : modes.length === 0 ? 'Allow at least one payment mode.'
    : value('max_return_days') != null && (value('default_return_days') ?? 0) > (value('max_return_days') ?? 0)
      ? 'Default days until return cannot exceed the maximum.'
    : null;

  const toggleType = (type: SettlementType, on: boolean) => {
    const patch: BorrowSettingsInput = type === 'item_return' ? { allow_item_return: on } : { allow_payment: on };
    // Keep the default pointing at an allowed type, as the server would.
    if (!on && defaultType === type) patch.default_settlement_type = type === 'item_return' ? 'payment' : 'item_return';
    set(patch);
  };

  const addReason = () => {
    const r = newReason.trim();
    if (!r || reasons.includes(r) || reasons.length >= 20) return;
    set({ reason_presets: [...reasons, r] });
    setNewReason('');
  };

  const apiError = update.error instanceof ApiError ? update.error.message : null;

  return (
    <div className="max-w-[900px] space-y-4 pb-24">
      {apiError && <div className="rounded-lg bg-danger-soft px-3 py-2 text-xs text-danger-ink">{apiError}</div>}

      {/* ── The switch ─────────────────────────────────────────── */}
      <Card className={cn('transition-colors', enabled && 'border-teal')}>
        <div className="flex flex-wrap items-start gap-4">
          <span className={cn('grid h-11 w-11 shrink-0 place-content-center rounded-full', enabled ? 'bg-accent text-teal' : 'bg-muted text-ink-muted')}>
            <ArrowLeftRight size={21} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">Medicine Borrow &amp; Return</div>
            <p className="mt-1 max-w-xl text-[12.5px] leading-relaxed text-ink-muted">
              Records medicine given to another hospital in an emergency, and tracks it until the{' '}
              <b>same item is returned</b> or its <b>value is paid</b>. Staff need the
              “Pharmacy — borrow &amp; return” permission (Users &amp; Roles) to use it.
            </p>
            <p className={cn('mt-2 text-[12px] font-medium', enabled ? 'text-success-ink' : 'text-ink-muted')}>
              {enabled
                ? 'On — staff can issue medicine to other hospitals.'
                : 'Off — no new issues. Medicine already lent can still be returned or paid for.'}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2.5">
            <span className="text-[12px] font-semibold text-ink-muted">{enabled ? 'On' : 'Off'}</span>
            <Switch checked={enabled} disabled={update.isPending} onCheckedChange={(v) => save({ enabled: v }, false)} />
          </div>
        </div>
      </Card>

      <div className="grid items-start gap-4 min-[900px]:grid-cols-2">
        {/* ── Settlement ───────────────────────────────────────── */}
        <Card>
          <CardHeader><CardTitle><Scale size={15} /> Settlement</CardTitle></CardHeader>
          <Row label={SETTLEMENT_LABEL.item_return} hint="The borrowing hospital gives back the same medicine.">
            <Switch checked={allowReturn} onCheckedChange={(v) => toggleType('item_return', v)} />
          </Row>
          <Row label={SETTLEMENT_LABEL.payment} hint="The borrowing hospital pays the item value.">
            <Switch checked={allowPayment} onCheckedChange={(v) => toggleType('payment', v)} />
          </Row>
          <Row label="Default on new issues" hint="Pre-selected on the issue form.">
            <SegmentedControl<SettlementType>
              size="sm"
              value={defaultType}
              onChange={(v) => set({ default_settlement_type: v })}
              options={[
                ...(allowReturn ? [{ value: 'item_return' as const, label: 'Return' }] : []),
                ...(allowPayment ? [{ value: 'payment' as const, label: 'Payment' }] : []),
              ]}
            />
          </Row>
          <Row label="Accepted payment modes" hint="For records settled by payment.">
            <SegmentedControl<PaymentMode>
              multiple
              size="sm"
              value={modes}
              onChange={(v) => set({ allowed_payment_modes: ALL_MODES.filter((m) => v.includes(m)) })}
              options={ALL_MODES.map((m) => ({ value: m, label: m }))}
            />
          </Row>
        </Card>

        {/* ── Issuing ──────────────────────────────────────────── */}
        <Card>
          <CardHeader><CardTitle><CalendarClock size={15} /> Issuing</CardTitle></CardHeader>
          <Row label="Default days until return" hint="Pre-fills the expected return date.">
            <NumberBox value={String(value('default_return_days') ?? 7)} onChange={(v) => set({ default_return_days: Math.max(0, parseInt(v, 10) || 0) })} suffix="days" />
          </Row>
          <Row label="Maximum days until return" hint="Blank = no limit.">
            <NumberBox value={value('max_return_days') == null ? '' : String(value('max_return_days'))} onChange={(v) => set({ max_return_days: optionalInt(v) })} suffix="days" placeholder="No limit" />
          </Row>
          <Row label="Maximum quantity per issue" hint="Blank = no limit (stock is always the ceiling).">
            <NumberBox value={value('max_quantity_per_issue') == null ? '' : String(value('max_quantity_per_issue'))} onChange={(v) => set({ max_quantity_per_issue: optionalInt(v) })} suffix="units" placeholder="No limit" />
          </Row>
          <Row label="Item value based on" hint="Purchase price falls back to MRP when a batch has none recorded.">
            <NativeSelect className="w-auto" value={value('value_basis')} onChange={(e) => set({ value_basis: e.target.value as BorrowSettings['value_basis'] })}>
              <option value="mrp">MRP × quantity</option>
              <option value="purchase_price">Purchase price × quantity</option>
            </NativeSelect>
          </Row>
          <Row label="Staff may change the value" hint="Off = the calculated value is fixed.">
            <Switch checked={value('allow_value_override') ?? true} onCheckedChange={(v) => set({ allow_value_override: v })} />
          </Row>
          <Row label="Contact person required">
            <Switch checked={value('require_contact_person') ?? false} onCheckedChange={(v) => set({ require_contact_person: v })} />
          </Row>
          <Row label="Contact number required">
            <Switch checked={value('require_contact_number') ?? false} onCheckedChange={(v) => set({ require_contact_number: v })} />
          </Row>
        </Card>

        {/* ── Returns ──────────────────────────────────────────── */}
        <Card>
          <CardHeader><CardTitle><PackageCheck size={15} /> Taking stock back</CardTitle></CardHeader>
          <Row label="Accept a different batch" hint="Same medicine, another batch. Off = only the batch that was lent.">
            <Switch checked={value('allow_different_batch_return') ?? true} onCheckedChange={(v) => set({ allow_different_batch_return: v })} />
          </Row>
          <Row label="Minimum shelf life left" hint="Expired stock is always refused. 0 = any unexpired stock.">
            <NumberBox value={String(value('min_return_expiry_days') ?? 0)} onChange={(v) => set({ min_return_expiry_days: Math.max(0, parseInt(v, 10) || 0) })} suffix="days" />
          </Row>
        </Card>

        {/* ── Reasons ──────────────────────────────────────────── */}
        <Card>
          <CardHeader><CardTitle><MessageSquareText size={15} /> Preset reasons</CardTitle></CardHeader>
          <p className="-mt-1 mb-2.5 text-[11.5px] text-ink-muted">Offered as one-tap choices on the issue form. Staff can always type their own.</p>
          <div className="mb-2.5 flex flex-wrap gap-1.5">
            {reasons.length === 0 && <span className="text-[12px] text-ink-muted">None — staff type every reason.</span>}
            {reasons.map((r) => (
              <span key={r} className="inline-flex items-center gap-1 rounded-full border border-border bg-canvas py-0.5 pl-2.5 pr-1 text-[12px]">
                {r}
                <button type="button" aria-label={`Remove ${r}`} className="rounded-full p-0.5 text-ink-muted hover:bg-muted"
                  onClick={() => set({ reason_presets: reasons.filter((x) => x !== r) })}>
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <Input value={newReason} maxLength={200} placeholder="Add a reason" onChange={(e) => setNewReason(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addReason(); } }} />
            <Button variant="ghost" size="sm" className="h-9" disabled={!newReason.trim() || reasons.length >= 20} onClick={addReason}>
              <Plus size={13} /> Add
            </Button>
          </div>
        </Card>
      </div>

      <div className="flex gap-2.5 rounded-lg bg-info-soft px-3 py-2.5 text-[11.5px] text-info-ink">
        <Info size={15} className="mt-px shrink-0" />
        <p className="leading-relaxed">
          Changes apply from the next issue, return or payment. Records already issued keep their value,
          dates and settlement type. Every change is recorded in the audit log
          {settings.updated_by ? <> — last changed by <b>{settings.updated_by}</b>{settings.updated_at ? ` on ${new Date(settings.updated_at).toLocaleString('en-IN')}` : ''}</> : ''}.
        </p>
      </div>

      {/* ── Save bar (same as Loyalty Points) ──────────────────── */}
      {(dirty || saved) && (
        <div className="sticky bottom-4 z-10">
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 shadow-card">
            {dirty ? (
              <>
                <span className={cn('text-[12.5px] font-medium', localError && 'text-danger-ink')}>{localError ?? 'Unsaved changes'}</span>
                <div className="ml-auto flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => { setDraft({}); setSaved(false); update.reset(); }}>Discard</Button>
                  <Button size="sm" disabled={!!localError} loading={update.isPending} onClick={() => save(draft, true)}>
                    Save borrow &amp; return rules
                  </Button>
                </div>
              </>
            ) : (
              <span className="text-[12.5px] font-medium text-success-ink">✓ Saved.</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 border-b border-line py-2.5 last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-medium">{label}</div>
        {hint && <div className="text-[11px] leading-snug text-ink-muted">{hint}</div>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function NumberBox({ value, onChange, suffix, placeholder }: { value: string; onChange: (v: string) => void; suffix: string; placeholder?: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <Input type="number" min={0} className="h-8 w-24 tabular-nums" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      <span className="text-[11.5px] text-ink-muted">{suffix}</span>
    </div>
  );
}
